/**
 * Pets canvas: the Christmas card maker and the gift card, drawn in the browser. The card maker section
 * (sections/pets-card-maker.liquid) loads it with the page; the gift note (<pets-gift-note> in
 * assets/pets-product-page.js) loads it on its first "Download gift card" click when it is not here yet.
 *
 * Nothing here sends anything anywhere. A photo is read with URL.createObjectURL into an <img> (its address is
 * revoked when another photo replaces it), drawn on a canvas, and a download is the canvas's toBlob saved through
 * an <a download> click. Names and notes go on the card with fillText, so they are only ever text.
 *
 *   Lumi.petsCanvas.drawCard(canvas, { photo, frame, name, mascot })
 *     The Christmas card (1080 x 1350): frame 'snow', 'candy' or 'ginger'; photo an <img>, cropped to fill the
 *     photo window; without one, the mascot <img> (if given) stands in the window. The name gives "love, Mochi",
 *     no name "love, the whole pack".
 *   Lumi.petsCanvas.giftCard({ note, name, productTitle }) -> Promise
 *     The gift card (1200 x 800) with the note in a hand font, saved as gift-card.png.
 *   Lumi.petsCanvas.fileName(name, pet)
 *     "<name or pet>-christmas-card.png": lower case, spaces to "-", only a-z, 0-9 and "-" kept.
 *
 * <pets-card-maker> (sections/pets-card-maker.liquid)
 *   The photo field, the name (the pet profile's until the shopper types one; it follows "lumi:pets:changed"),
 *   the frame buttons (aria-pressed) and "Download card", which waits for the fonts before it saves.
 * The words come from Lumi.strings, filled from the locale by the block and the section.
 */
(function () {
  const Lumi = (window.Lumi = window.Lumi || {});
  // The card maker and the gift note can both load this file; the second copy stops here.
  if (Lumi.petsCanvas) return;

  const str = (key, fallback) => (Lumi.strings && typeof Lumi.strings[key] === 'string' ? Lumi.strings[key] : fallback);
  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match));
  const clean = (name) => (Lumi.petsCore ? Lumi.petsCore.cleanName(name) : String(name || '').replace(/[<>]/g, '').trim().slice(0, 20));

  const COCOA = '#4A2C17';
  const DAWN = '#FFF6EA';
  const CREAM = '#FFF4E0';
  const BLUSH = '#A8406A';
  const LILAC = '#E9CCFF';
  const WHITE = '#FFFFFF';
  // The three frames, in the colors of the swatches on their buttons.
  const FRAMES = {
    snow: { ground: '#D3E8FB', ink: COCOA, love: BLUSH, window: DAWN },
    candy: { ground: WHITE, ink: COCOA, love: BLUSH, window: DAWN, stripe: '#C8323C' },
    ginger: { ground: '#B87843', ink: CREAM, love: CREAM, window: '#D89A62', icing: CREAM },
  };
  const DISPLAY = 'Fredoka, "Arial Rounded MT Bold", sans-serif';
  const HAND = '"Gochi Hand", "Segoe Print", cursive';
  const PET_WORDS = { dog: 'dog', cat: 'cat' };

  // The self-hosted fonts load when first used, so a card waits for them (or for their failure) before it is saved.
  let fonts = null;
  function fontsReady() {
    if (!fonts) {
      fonts =
        document.fonts && document.fonts.load
          ? Promise.all(['500 26px Fredoka', '600 104px Fredoka', '72px "Gochi Hand"'].map((f) => document.fonts.load(f))).then(
              () => {},
              () => {}
            )
          : Promise.resolve();
    }
    return fonts;
  }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // One centered line, in a smaller size until it fits the width.
  function line(ctx, text, x, y, width, weight, size, family) {
    let px = size;
    ctx.font = `${weight} ${px}px ${family}`;
    while (px > size / 2 && ctx.measureText(text).width > width) {
      px -= 2;
      ctx.font = `${weight} ${px}px ${family}`;
    }
    ctx.fillText(text, x, y);
  }

  function drawCard(canvas, opts) {
    const o = opts || {};
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const key = Object.prototype.hasOwnProperty.call(FRAMES, o.frame) ? o.frame : 'snow';
    const f = FRAMES[key];
    ctx.save();
    ctx.fillStyle = f.ground;
    ctx.fillRect(0, 0, W, H);
    if (key === 'candy') {
      // Red stripes round the edge of a white card.
      ctx.save();
      ctx.strokeStyle = f.stripe;
      ctx.lineWidth = 36;
      for (let k = -H; k < W + H; k += 96) {
        ctx.beginPath();
        ctx.moveTo(k, 0);
        ctx.lineTo(k + H, H);
        ctx.stroke();
      }
      ctx.restore();
      ctx.fillStyle = WHITE;
      roundRect(ctx, 54, 54, W - 108, H - 108, 46);
      ctx.fill();
    } else if (key === 'snow') {
      ctx.fillStyle = WHITE;
      for (let s = 0; s < 70; s += 1) {
        ctx.beginPath();
        ctx.arc((s * 137) % W, (s * 251) % H, 4 + (s % 5) * 2, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      // Gingerbread: a line of icing dots round the edge.
      ctx.save();
      ctx.strokeStyle = f.icing;
      ctx.lineWidth = 10;
      ctx.lineCap = 'round';
      ctx.setLineDash([2, 26]);
      roundRect(ctx, 40, 40, W - 80, H - 80, 60);
      ctx.stroke();
      ctx.restore();
    }

    // The photo window with a white edge.
    const px = 120;
    const py = 150;
    const pw = W - 240;
    const ph = 820;
    ctx.save();
    roundRect(ctx, px, py, pw, ph, 60);
    ctx.clip();
    ctx.fillStyle = f.window;
    ctx.fillRect(px, py, pw, ph);
    const photo = o.photo;
    const iw = photo ? photo.naturalWidth || photo.width : 0;
    const ih = photo ? photo.naturalHeight || photo.height : 0;
    if (iw && ih) {
      const scale = Math.max(pw / iw, ph / ih);
      ctx.drawImage(photo, px + (pw - iw * scale) / 2, py + (ph - ih * scale) / 2, iw * scale, ih * scale);
    } else if (o.mascot && o.mascot.naturalWidth) {
      const mw = pw * 0.84;
      const mh = (mw * o.mascot.naturalHeight) / o.mascot.naturalWidth;
      ctx.drawImage(o.mascot, px + (pw - mw) / 2, py + ph - mh - 24, mw, mh);
    }
    ctx.restore();
    ctx.lineWidth = 14;
    ctx.strokeStyle = WHITE;
    roundRect(ctx, px, py, pw, ph, 60);
    ctx.stroke();

    const name = clean(o.name);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = f.ink;
    line(ctx, str('petsCardMerry', 'Merry Christmas'), W / 2, 1100, W - 160, 600, 104, DISPLAY);
    ctx.fillStyle = f.love;
    const love = name ? fill(str('petsCardLoveName', 'love, {name}'), { name }) : str('petsCardLoveAll', 'love, the whole pack');
    line(ctx, love, W / 2, 1200, W - 160, 400, 72, HAND);
    ctx.fillStyle = f.ink;
    ctx.globalAlpha = 0.7;
    line(ctx, fill(str('petsCardMadeAt', 'made at {shop}'), { shop: str('petsShop', '') }), W / 2, 1280, W - 200, 500, 26, DISPLAY);
    ctx.restore();
  }

  // The note's lines at the current font: its own line breaks first, then words, and a word wider than a line by letters.
  function wrap(ctx, text, width) {
    const lines = [];
    String(text)
      .split(/\r?\n/)
      .forEach((paragraph) => {
        let current = '';
        paragraph
          .split(/\s+/)
          .filter(Boolean)
          .forEach((word) => {
            const joined = current ? `${current} ${word}` : word;
            if (ctx.measureText(joined).width <= width) {
              current = joined;
              return;
            }
            if (current) lines.push(current);
            current = '';
            for (const letter of word) {
              if (current && ctx.measureText(current + letter).width > width) {
                lines.push(current);
                current = letter;
              } else {
                current += letter;
              }
            }
          });
        lines.push(current);
      });
    return lines;
  }

  // Saves the canvas as a PNG through a link click. The image never leaves the browser.
  function save(canvas, filename) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas export failed'));
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.hidden = true;
        document.body.append(link);
        link.click();
        link.remove();
        // Long enough for the browser to start the download.
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        resolve(filename);
      }, 'image/png');
    });
  }

  function giftCard(opts) {
    const o = opts || {};
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 800;
    return fontsReady().then(() => {
      const ctx = canvas.getContext('2d');
      const W = canvas.width;
      ctx.fillStyle = DAWN;
      ctx.fillRect(0, 0, W, canvas.height);
      ctx.strokeStyle = LILAC;
      ctx.lineWidth = 40;
      ctx.strokeRect(20, 20, W - 40, canvas.height - 40);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = COCOA;
      const name = clean(o.name);
      const title = name ? fill(str('petsGiftFor', 'A little something for {name}'), { name }) : str('petsGiftForYou', 'A little something for you');
      line(ctx, title, W / 2, 170, W - 160, 600, 72, DISPLAY);

      // The note fills a box in the middle, in a smaller hand when it is long.
      const note = String(o.note || '').trim() || str('petsGiftLove', 'Made with love.');
      const top = 230;
      const room = 420;
      let size = 46;
      let lines = [];
      for (;;) {
        ctx.font = `400 ${size}px ${HAND}`;
        lines = wrap(ctx, note, W - 240);
        if (lines.length * size * 1.4 <= room || size <= 24) break;
        size -= 2;
      }
      const lead = size * 1.4;
      lines = lines.slice(0, Math.max(1, Math.floor(room / lead)));
      let y = top + (room - lines.length * lead) / 2 + size;
      lines.forEach((text) => {
        ctx.fillText(text, W / 2, y);
        y += lead;
      });

      const shop = str('petsShop', '');
      const footer = o.productTitle ? fill(str('petsGiftFooter', '{product} · {shop}'), { product: o.productTitle, shop }) : shop;
      ctx.globalAlpha = 0.7;
      line(ctx, footer, W / 2, 720, W - 200, 500, 28, DISPLAY);
      ctx.globalAlpha = 1;
      return save(canvas, 'gift-card.png');
    });
  }

  const slug = (text) =>
    String(text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  function fileName(name, pet) {
    return `${slug(clean(name)) || PET_WORDS[pet] || 'pet'}-christmas-card.png`;
  }

  // The pup or the kitten from the page's mascot symbols (snippets/pets-mascot.liquid), as an image a canvas can draw.
  const mascots = {};
  function mascot(pet) {
    const id = pet === 'cat' ? 'pets-kitten' : 'pets-pup';
    if (!mascots[id]) {
      mascots[id] = new Promise((resolve) => {
        const symbol = document.getElementById(id);
        if (!symbol) {
          resolve(null);
          return;
        }
        const box = symbol.getAttribute('viewBox') || '0 0 140 104';
        const [, , w, h] = box.split(/[\s,]+/).map(Number);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${box}" width="${w * 5}" height="${h * 5}">${symbol.innerHTML}</svg>`;
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      });
    }
    return mascots[id];
  }

  class PetsCardMaker extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.canvas = this.querySelector('[data-pets-card-canvas]');
      this.photoInput = this.querySelector('[data-pets-card-photo]');
      this.nameInput = this.querySelector('[data-pets-card-name]');
      this.downloadBtn = this.querySelector('[data-pets-card-download]');
      this.statusEl = this.querySelector('[data-pets-card-status]');
      this.frameBtns = [...this.querySelectorAll('[data-pets-card-frame]')];
      if (!this.canvas || !this.canvas.getContext || !this.nameInput || !this.downloadBtn) return;
      this._ready = true;
      const pressed = this.frameBtns.find((b) => b.getAttribute('aria-pressed') === 'true');
      this.frame = (pressed && pressed.dataset.petsCardFrame) || this.dataset.frame || 'snow';
      this.photo = null;
      this.photoUrl = '';
      this.photoTurn = 0;
      this.mascot = null;
      this.noMascot = false;
      this.typed = false;
      this.profile = Lumi.pet ? Lumi.pet.get() : { pet: 'all', name: '' };
      if (!this.nameInput.value) this.nameInput.value = this.profile.name || '';

      this.onClick = this.onClick.bind(this);
      this.onName = this.onName.bind(this);
      this.onPhoto = this.onPhoto.bind(this);
      this.onPet = this.onPet.bind(this);
      this.addEventListener('click', this.onClick);
      this.nameInput.addEventListener('input', this.onName);
      if (this.photoInput) this.photoInput.addEventListener('change', this.onPhoto);
      document.addEventListener('lumi:pets:changed', this.onPet);

      this.hidden = false;
      this.draw();
      fontsReady().then(() => {
        if (this._ready) this.draw();
      });
      this.loadMascot();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.removeEventListener('click', this.onClick);
      this.nameInput.removeEventListener('input', this.onName);
      if (this.photoInput) this.photoInput.removeEventListener('change', this.onPhoto);
      document.removeEventListener('lumi:pets:changed', this.onPet);
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = '';
      this.photo = null;
    }

    draw() {
      drawCard(this.canvas, {
        photo: this.photo,
        frame: this.frame,
        name: this.nameInput.value,
        mascot: this.noMascot ? null : this.mascot,
      });
    }

    loadMascot() {
      const pet = this.profile.pet;
      mascot(pet).then((img) => {
        if (!this._ready || this.profile.pet !== pet) return;
        this.mascot = img;
        this.draw();
      });
    }

    onClick(e) {
      const frame = e.target.closest('[data-pets-card-frame]');
      if (frame && this.contains(frame)) {
        this.frame = frame.dataset.petsCardFrame;
        this.frameBtns.forEach((b) => b.setAttribute('aria-pressed', String(b === frame)));
        this.draw();
        return;
      }
      if (e.target.closest('[data-pets-card-download]')) this.download();
    }

    // A name typed here stays on the card, whatever the profile says later.
    onName() {
      this.typed = true;
      this.draw();
    }

    // Until a name is typed, the card takes the profile's; the pet picks the pup or the kitten.
    onPet() {
      const profile = Lumi.pet.get();
      const petMoved = profile.pet !== this.profile.pet;
      this.profile = profile;
      if (!this.typed) this.nameInput.value = profile.name || '';
      if (petMoved) {
        this.mascot = null;
        this.loadMascot();
      }
      this.draw();
    }

    onPhoto() {
      const file = this.photoInput.files && this.photoInput.files[0];
      if (!file) return;
      const turn = (this.photoTurn += 1);
      if (!/^image\//.test(file.type)) {
        this.say(str('petsCardPhotoError', ''));
        return;
      }
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        if (!this._ready || turn !== this.photoTurn) {
          URL.revokeObjectURL(url);
          return;
        }
        // The photo this one replaces lets go of its address.
        if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
        this.photoUrl = url;
        this.photo = img;
        this.say('');
        this.draw();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        if (turn === this.photoTurn) this.say(str('petsCardPhotoError', ''));
      };
      img.src = url;
    }

    // Cleared first and set on the next frame, so the same message twice is heard twice.
    say(message) {
      if (!this.statusEl) return;
      const turn = (this.sayTurn = (this.sayTurn || 0) + 1);
      this.statusEl.textContent = '';
      if (!message) return;
      window.requestAnimationFrame(() => {
        if (turn === this.sayTurn) this.statusEl.textContent = message;
      });
    }

    async download() {
      if (this.busy) return;
      this.busy = true;
      this.downloadBtn.setAttribute('aria-busy', 'true');
      this.say('');
      const name = fileName(this.nameInput.value, this.profile.pet);
      try {
        await fontsReady();
        this.draw();
        try {
          await save(this.canvas, name);
        } catch (err) {
          // A browser that will not export a canvas holding the drawn mascot gets the card without it.
          if (!this.mascot || this.photo || this.noMascot) throw err;
          this.noMascot = true;
          this.draw();
          await save(this.canvas, name);
        }
      } catch (err) {
        this.say(str('petsCardSaveError', ''));
      } finally {
        this.busy = false;
        this.downloadBtn.removeAttribute('aria-busy');
      }
    }
  }

  Lumi.petsCanvas = { drawCard, giftCard, fileName };
  if (!customElements.get('pets-card-maker')) customElements.define('pets-card-maker', PetsCardMaker);
})();
